import { useContext, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'state/hooks';

import { ScenarioSetupUIMode } from 'appConstants';
import { ItemList, Scenario } from 'appTypes';
import { StlList } from 'features/common/StlList/StlList';
import StlRecentList from 'features/common/StlList/StlRecentList';

import {
  selectScenarioStatus,
  setViewAllModal,
  useGetScenariosQuery,
} from '../../state/detourSlices';
import {
  selectCurrentScenarioId,
  selectProject,
  selectScenarioSetupUIMode,
} from '../../state/workflowSlice';
import { MapContext } from '../map/MapContext';
import { fitBoundsToBbox, isGeometryOnMap } from '../map/utils/mapUtils';
import ScenarioCard from './ScenarioCard/ScenarioCard';
import { useSelectedScenarioClosureRoute } from './ScenarioDataProvider';
import { useScenarioUI } from './hooks/useScenarioUI';

export function ScenarioList({
  limit,
  highlightSelected,
  filters,
  wide,
  selectedOnly,
  excludeSelected,
}: ItemList) {
  const { refMap, mapReady } = useContext(MapContext);
  const dispatch = useAppDispatch();
  const uiMode = useAppSelector(selectScenarioSetupUIMode);
  const project = useAppSelector(selectProject);
  const { setScenariosMode } = useScenarioUI();
  const [pendingPanToClosure, setPendingPanToClosure] =
    useState<boolean>(false);
  const { currentData: scenarioRouteData } = useSelectedScenarioClosureRoute();

  const { currentData: pastScenarios, isLoading } = useGetScenariosQuery(
    project,
    { skip: !project },
  );

  useEffect(() => {
    if (scenarioRouteData && mapReady && pendingPanToClosure) {
      const { feature } = scenarioRouteData;
      if (!isGeometryOnMap(feature.geometry, refMap.current)) {
        fitBoundsToBbox(refMap.current, feature.geometry, 0.33);
      }
      setPendingPanToClosure(false);
    }
  }, [scenarioRouteData, mapReady]);

  const onScenarioSelectClick = (_e: React.MouseEvent, scenario: Scenario) => {
    dispatch(setViewAllModal(false));
    setScenariosMode(scenario);
    setPendingPanToClosure(true);
  };
  const currentScenarioId = useAppSelector(selectCurrentScenarioId);

  return (
    <StlList<Scenario>
      limit={limit}
      highlightSelected={highlightSelected}
      filters={filters}
      wide={wide}
      loading={isLoading}
      idAccessor="scenario_id"
      editing={uiMode === ScenarioSetupUIMode.Edit}
      filterBy="view_date"
      onItemClick={onScenarioSelectClick}
      currentItemId={currentScenarioId}
      data={pastScenarios?.scenarios}
      CardComponent={ScenarioCard}
      itemName="closure scenarios"
      selectedOnly={selectedOnly}
      excludeSelected={excludeSelected}
    />
  );
}

export function RecentScenarioList() {
  const dispatch = useAppDispatch();

  const project_slug = useAppSelector(selectProject);
  const currentScenarioId = useAppSelector(selectCurrentScenarioId);
  const uiMode = useAppSelector(selectScenarioSetupUIMode);
  const status = useAppSelector(selectScenarioStatus);
  const { currentData: pastScenarios } = useGetScenariosQuery(project_slug, {
    skip: !project_slug,
  });
  const onViewAll = () => {
    dispatch(setViewAllModal(true));
  };

  const items = pastScenarios?.scenarios ?? [];
  return (
    <StlRecentList<Scenario>
      idAccessor="scenario_id"
      status={status}
      viewMode={uiMode === ScenarioSetupUIMode.Scenarios}
      currentId={currentScenarioId}
      items={items}
      onViewAll={onViewAll}
      listComponent={ScenarioList}
    />
  );
}
